import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  ArrowTrendingDownIcon,
  BanknotesIcon,
  BoltIcon,
  BookmarkIcon,
  BuildingLibraryIcon,
  CalendarDaysIcon,
  ClockIcon,
  CreditCardIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  HandRaisedIcon,
  MoonIcon,
  ScaleIcon,
  ScissorsIcon,
  StarIcon,
  TagIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { fetchCustomerStatistics, fetchCustomersV2, useUiPreferences } from "@api";
import { buildApiQuery } from "@utils";

// A working set is a Trade Accounts filter pinned to Today's tasks. It lives in UiPreferences like
// a saved filter, but under its own type so the Trade Accounts "Saved Filters" menu never lists it.
export const WORKING_SET_TYPE = "workingSet:tradeAccounts";

export const WORKING_SET_ICONS = {
  bookmark: BookmarkIcon,
  hand: HandRaisedIcon,
  creditCard: CreditCardIcon,
  scale: ScaleIcon,
  warning: ExclamationTriangleIcon,
  clock: ClockIcon,
  envelope: EnvelopeIcon,
  banknotes: BanknotesIcon,
  scissors: ScissorsIcon,
  flag: FlagIcon,
  trendingDown: ArrowTrendingDownIcon,
  moon: MoonIcon,
  tag: TagIcon,
  calendar: CalendarDaysIcon,
  bank: BuildingLibraryIcon,
  people: UserGroupIcon,
  star: StarIcon,
  bolt: BoltIcon,
};
export const DEFAULT_WORKING_SET_ICON = "bookmark";
export const WORKING_SET_NAME_MAX = 40;

// Class names are spelled out in full so Tailwind keeps them.
export const WORKING_SET_COLOURS = {
  red: { tile: "bg-red-50 text-red-600", dot: "bg-red-600" },
  orange: { tile: "bg-orange-50 text-orange-600", dot: "bg-orange-600" },
  amber: { tile: "bg-amber-50 text-amber-600", dot: "bg-amber-600" },
  green: { tile: "bg-green-50 text-green-700", dot: "bg-green-700" },
  indigo: { tile: "bg-indigo-50 text-indigo-600", dot: "bg-indigo-600" },
  zinc: { tile: "bg-zinc-100 text-zinc-700", dot: "bg-zinc-700" },
};
export const DEFAULT_WORKING_SET_COLOUR = "zinc";

export const parseWorkingSet = (preference) => {
  let value;
  try {
    value = JSON.parse(preference.value);
  } catch {
    value = null;
  }
  return {
    id: preference.id,
    name: preference.name,
    createdOn: preference.createdOn,
    icon: WORKING_SET_ICONS[value?.icon] ? value.icon : DEFAULT_WORKING_SET_ICON,
    colour: WORKING_SET_COLOURS[value?.colour] ? value.colour : DEFAULT_WORKING_SET_COLOUR,
    order: Number.isFinite(value?.order) ? value.order : null,
    filters: Array.isArray(value?.filters) ? value.filters : [],
  };
};

// What Trade Accounts queries while a set is open: the set's filters plus the user's, where the
// set wins on a shared field so a stacked filter can only narrow it, never replace or widen it.
export const withWorkingSet = (baseFilters, userFilters) => {
  const baseKeys = new Set(baseFilters.map((f) => f.key));
  return [...baseFilters, ...userFilters.filter((f) => !baseKeys.has(f.key))];
};

export const serializeWorkingSet = ({ icon, colour, order, filters }) =>
  JSON.stringify({ icon, colour: colour ?? DEFAULT_WORKING_SET_COLOUR, order: order ?? null, filters });

const byOrderThenCreated = (a, b) => {
  if (a.order !== b.order) {
    if (a.order === null) return 1;
    if (b.order === null) return -1;
    return a.order - b.order;
  }
  return String(a.createdOn ?? "").localeCompare(String(b.createdOn ?? ""));
};

// Home lays working sets out on a fixed 5×2 board; a set's stored `order` is its slot, and slots
// can sit empty. Sets without a usable slot (none yet, out of range, or clashing with an earlier
// set) fill the first free slots, oldest first, so the board is stable for sets that predate slots.
export const WORKING_SET_COLUMNS = 5;
export const WORKING_SET_SLOTS = 10;

export const placeWorkingSets = (sets, slotCount = WORKING_SET_SLOTS) => {
  const slots = Array(slotCount).fill(null);
  const unplaced = [];
  [...sets].sort(byOrderThenCreated).forEach((set) => {
    const slot = set.order;
    if (Number.isInteger(slot) && slot >= 0 && slot < slotCount && !slots[slot]) slots[slot] = set;
    else unplaced.push(set);
  });
  unplaced.forEach((set) => {
    const free = slots.indexOf(null);
    if (free >= 0) slots[free] = set;
  });
  return slots;
};

// The board after dropping a set on `slot`: an empty slot just takes it, an occupied one swaps.
export const moveToSlot = (board, setId, slot) => {
  const from = board.findIndex((s) => s?.id === setId);
  if (from < 0 || from === slot) return board;
  const next = [...board];
  next[from] = board[slot];
  next[slot] = board[from];
  return next;
};

export const firstFreeSlot = (sets) => {
  const free = placeWorkingSets(sets).indexOf(null);
  return free >= 0 ? free : null;
};

export const useWorkingSetList = () => {
  const { data: preferences = [], isLoading } = useUiPreferences();
  const sets = useMemo(
    () =>
      preferences
        .filter((p) => p.type === WORKING_SET_TYPE)
        .map(parseWorkingSet)
        .sort(byOrderThenCreated),
    [preferences],
  );
  return { sets, isLoading };
};

// Live figures for many filter sets come from one batched statistics call: the same searches
// Trade Accounts runs, statistics only. Results are keyed by query string, so reordering sets never
// refetches, and while a changed batch loads the figures already known stay on screen.
const statsQueryString = (filters) => buildApiQuery({ filters });

const useStatsBatch = (queries) => {
  const unique = [...new Set(queries)].sort();
  return useQuery({
    queryKey: ["accountStatsBatch", unique],
    queryFn: async ({ signal }) => {
      const results = await fetchCustomerStatistics(unique, { signal });
      return Object.fromEntries(unique.map((q, i) => [q, results[i]]));
    },
    enabled: unique.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
};

// Per filter list: { isLoading, isError, count, overdue, statistics }.
export const useFilterStats = (filterLists) => {
  const queries = filterLists.map(statsQueryString);
  const batch = useStatsBatch(queries);
  return queries.map((q) => {
    const statistics = batch.data?.[q];
    return {
      isLoading: !statistics && (batch.isLoading || batch.isFetching),
      isError: !statistics && batch.isError,
      count: statistics?.all ?? null,
      overdue: statistics?.allOverdue ?? null,
      statistics: statistics ?? null,
    };
  });
};

// Home's figures for every working set in one request.
export const useWorkingSets = () => {
  const { sets, isLoading } = useWorkingSetList();
  const stats = useFilterStats(sets.map((s) => s.filters));
  return {
    isLoading,
    sets: sets.map((set, i) => ({ ...set, ...stats[i] })),
  };
};

const useDebounced = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

// The builder's live preview: the totals the set would hold. Debounced
// so typing an amount doesn't fire a search per keystroke; the last result stays up while the next
// one loads so the panel never flashes empty.
export const useWorkingSetPreview = (filters, { enabled = true } = {}) => {
  const query = useDebounced(
    buildApiQuery({ filters, page: 1, pageSize: 1 }),
    300,
  );
  const result = useQuery({
    queryKey: ["workingSetPreview", query],
    queryFn: ({ signal }) => fetchCustomersV2(query, { signal }),
    enabled: enabled && filters.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
  return {
    isFetching: result.isFetching,
    isError: result.isError,
    hasData: !!result.data,
    count: result.data?.rows ?? null,
    overdue: result.data?.statistics?.allOverdue ?? null,
  };
};
